import type { Instant } from '@js-joda/core';
/*
 * The main-thread `session` relay (ADR 0016 `workerSessionModule`, spawning side): computes the
 * effective session snapshot — masquerade-aware access token, session state, and the superuser
 * expiration — and streams it to workers that compose the `session` key. `connectWorker`
 * resolves this under the `WorkerStateRelay:session` binding, captures the synchronous first
 * emission into `init.state`, and forwards every later emission as a push. Clears are pushes
 * like any other: `accessToken: null` and `phase: 'expired'` reach the worker mechanically.
 */
import { effect } from '@preact/signals-react';
import { isEqual } from 'es-toolkit';
import { inject, injectable } from 'inversify';
import type { WorkerSessionSnapshot, WorkerStateRelay } from 'src/services/workerHost/stateRegistry';
import type { ReactBindings } from 'src/types/bindings';

const instantsEqual = (a: Instant | undefined, b: Instant | undefined): boolean =>
    a === b || (a !== undefined && b !== undefined && a.equals(b));

const snapshotsEqual = (a: WorkerSessionSnapshot, b: WorkerSessionSnapshot): boolean =>
    a.accessToken === b.accessToken &&
    isEqual(a.state, b.state) &&
    instantsEqual(a.superuserAccessEnabledUntil, b.superuserAccessEnabledUntil);

@injectable()
export class SessionWorkerStateRelay implements WorkerStateRelay<'session'> {
    readonly key = 'session' as const;

    readonly #store: ReactBindings['Store'];
    readonly #sessionService: ReactBindings['SessionService'];

    constructor(
        @inject('Store') store: ReactBindings['Store'],
        @inject('SessionService') sessionService: ReactBindings['SessionService'],
    ) {
        this.#store = store;
        this.#sessionService = sessionService;
    }

    /** Masquerade wins while active, exactly as the main-thread trading service resolved its effective token. */
    #snapshot(): WorkerSessionSnapshot {
        const state = this.#store.getState();
        return {
            state: this.#sessionService.state.value,
            accessToken: state.auth?.masqAccessToken?.token ?? this.#sessionService.accessToken.value,
            superuserAccessEnabledUntil: state.ui.superuserAccessEnabledUntil,
        };
    }

    connect(send: (payload: WorkerSessionSnapshot) => void): Disposable {
        // Transactional: the synchronous first emission runs before any subscription is
        // registered, so a throwing receiver leaves nothing behind to leak.
        let last = this.#snapshot();
        send(last);

        const stack = new DisposableStack();
        const sync = (): void => {
            const next = this.#snapshot();
            if (snapshotsEqual(last, next)) {
                return;
            }
            last = next;
            send(next);
        };
        stack.defer(this.#store.subscribeSelector((state) => state.auth).handle(sync));
        stack.defer(this.#store.subscribeSelector((state) => state.ui.superuserAccessEnabledUntil).handle(sync));
        stack.defer(
            effect(() => {
                void this.#sessionService.state.value;
                void this.#sessionService.accessToken.value;
                sync();
            }),
        );
        return stack;
    }
}
